import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import React, { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { ScrollToTop } from '../../../common/table/helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import styles from './VisitCardLineTable.module.scss';
import InfoIcon from '@lmt/unicorn-shared/app/icons/info.svg?react';
import DragAndDropIcon from '@lmt/unicorn-shared/app/icons/drag-and-drop.svg?react';
import CancelIcon from '@lmt/unicorn-shared/app/icons/cancel.svg?react';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from '../../../common/ui-kit/IconButton';
import { CheckBox } from '../../../common/ui-kit/CheckBox';
import { Input } from '../../../common/ui-kit/inputs/Input';
import { ComboBoxInput } from '../../../common/ui-kit/inputs/dropdown/ComboBoxInput';
import { VisitCardLineStyleDto, VisitCardSettingsDto } from '@lmt/shared-types';
import i18next from 'i18next';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import clsx from 'clsx';
import { UseFormReturn } from 'react-hook-form';

const fontSizeOptions = [10, 11, 12, 13, 14, 15, 16, 20, 24, 32, 36];
const paddingTopOptions = [
  2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24, 28, 32, 36, 40,
];

const DragHandle = React.forwardRef<HTMLDivElement>((props, ref) => (
  <div
    ref={ref}
    {...props}
    title={i18next.t('calendar.visit_card_settings.list.drag_and_drop_tooltip')}
  >
    <DragAndDropIcon className={styles.icon} />
  </div>
));

const SortableRowWrapper = (props: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.data.position + 1 });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <Row
      {...props}
      {...attributes}
      listeners={listeners}
      style={style}
      ref={setNodeRef}
    />
  );
};

const Row = React.forwardRef<HTMLDivElement, RowProps>((props, ref) => {
  const {
    data,
    removeLine,
    editLine,
    expressionErrors,
    listeners,
    style,
    form,
    lineStyleKey,
    ...other
  } = props;
  const [expression, setExpression] = useState<string>(data.expression);
  useEffect(() => {
    setExpression(data.expression);
  }, [data.expression]);

  const { formState } = form;

  return (
    <div
      {...other}
      className={clsx(styles.row, styles.rowData)}
      style={style}
      data-test-id="visit-card-settings-table-row"
    >
      <div>
        <DragHandle ref={ref} {...listeners} />
      </div>
      <div className={styles.cell}>
        <Input
          value={expression}
          onChange={(value) => setExpression(value.currentTarget.value)}
          errorText={
            (
              formState.errors?.[lineStyleKey]?.[data.position]
                ?.expression as any
            )?.message ??
            (expressionErrors?.includes(data.expression)
              ? i18next.t(
                  'calendar.visit_card_settings.errors.invalid_expression',
                )
              : '')
          }
          onBlur={() => editLine({ ...data, expression: expression })}
          testId="visit-card-settings-table-line-field"
        />
      </div>
      <div className={styles.cell}>
        <DropDownInput
          variant={'formInput'}
          options={fontSizeOptions}
          value={data.fontSize}
          getOptionLabel={(x) => x.toString()}
          required={true}
          enableSearch={true}
          onValueChanged={(value) => editLine({ ...data, fontSize: value })}
          errorText={
            (formState.errors?.[lineStyleKey]?.[data.position]?.fontSize as any)
              ?.message
          }
          testId="visit-card-settings-table-font-size"
        />
      </div>
      <div className={styles.cell}>
        <ComboBoxInput
          options={paddingTopOptions}
          value={data.paddingTop}
          labelFunction={(x) => x.toString()}
          onValueChanged={(value, option) =>
            value
              ? editLine({ ...data, paddingTop: parseInt(value) })
              : editLine({ ...data, paddingTop: option! })
          }
          variant={'formInput'}
          errorText={
            (
              formState.errors?.[lineStyleKey]?.[data.position]
                ?.paddingTop as any
            )?.message
          }
          testId="visit-card-settings-table-top-padding"
        />
      </div>
      <div
        className={clsx(styles.cell, styles.isBoldCell)}
        data-test-id="visit-card-settings-table-bold-checkbox"
      >
        <CheckBox
          name={`fontWeight${data.position}`}
          title="Bold"
          checked={data.isBold}
          onChange={(e) =>
            editLine({
              ...data,
              isBold: e.currentTarget.checked,
            })
          }
          className={styles.checkbox}
        />
      </div>

      <div
        className={clsx(styles.cell, styles.deleteCell)}
        onClick={(event: any) => event.stopPropagation()}
      >
        <IconButton
          color="grey"
          iconComponent={<CancelIcon className={styles.optionIcon} />}
          onClick={() => removeLine(data.position)}
          testId="visit-card-settings-table-delete-button"
        />
      </div>
    </div>
  );
});

type Props = {
  isNoRulesFound: boolean;
  data: VisitCardLineStyleDto[];
  onEdit: (line: VisitCardLineStyleDto) => void;
  onRemove: (position: number) => void;
  onSortEnd: (e: DragEndEvent) => void;
  expressionErrors: string[];
  form: UseFormReturn<VisitCardSettingsDto>;
  lineStyleKey: keyof VisitCardSettingsDto;
};
type RowProps = {
  data: VisitCardLineStyleDto;
  editLine: (line: VisitCardLineStyleDto) => void;
  removeLine: (position: number) => void;
  expressionErrors?: string[];
  style?: CSSProperties | undefined;
  listeners?: any;
  form: UseFormReturn<VisitCardSettingsDto>;
  lineStyleKey: keyof VisitCardSettingsDto;
};
export const VisitCardLineTable: FC<Props> = React.memo(
  function VisitCardLineTable(props) {
    const i18next = useScopedTranslation('calendar.visit_card_settings.list');
    const sensors = useSensors(useSensor(PointerSensor));
    const [activeId, setActiveId] = useState<number | null>(null);

    const tableRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [props.data]);

    if (props.isNoRulesFound) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>
            {i18next.t('empty_list_text')}
          </div>
        </div>
      );
    }

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => {
          setActiveId(e.active.id as number);
        }}
        onDragEnd={(e) => {
          props.onSortEnd(e);
          setActiveId(null);
        }}
      >
        <div className={clsx(styles.container)}>
          <div className={clsx(styles.header, styles.row)}>
            <div className={styles.info} title={i18next.t('info_tooltip')}>
              <InfoIcon className={styles.infoIcon} />
            </div>
            <div className={styles.headerCell}>
              {i18next.t('columns.expression')}
            </div>
            <div className={styles.headerCell}>
              {i18next.t('columns.font_size')}
            </div>
            <div className={styles.headerCell}>
              {i18next.t('columns.padding_top')}
            </div>
            <div></div>
            <div></div>
          </div>
          <SortableContext
            items={props.data.map((x) => x.position + 1)}
            strategy={verticalListSortingStrategy}
          >
            {props.data.map((row) => (
              <SortableRowWrapper
                key={row.position}
                data={row}
                editLine={props.onEdit}
                removeLine={props.onRemove}
                expressionErrors={props.expressionErrors}
                form={props.form}
                lineStyleKey={props.lineStyleKey}
              />
            ))}
          </SortableContext>
        </div>
      </DndContext>
    );
  },
);
